import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getOnboardingOAuthLogin } from "~/api/v2/requests";
import { Alert, AlertDescription } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { setLegacyMigrationCompleted } from "~/molecules/onboard-microsoft/LegacyMigrationCard";

interface Props {
  setupToken: string;
  onSkip: () => void;
}

export function SignInCard({ setupToken, onSkip }: Props) {
  const { t } = useTranslation();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  const handleSignIn = async () => {
    setIsSigningIn(true);
    setSignInError(null);

    try {
      const { redirectUrl } = await getOnboardingOAuthLogin(setupToken);
      window.location.assign(redirectUrl);
    } catch {
      setSignInError(t(($) => $.msIntegration.signIn.error));
      setIsSigningIn(false);
    }
  };

  const handleSkip = () => {
    setLegacyMigrationCompleted();
    onSkip();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(($) => $.msIntegration.signIn.title)}</CardTitle>
        <CardDescription>
          {t(($) => $.msIntegration.signIn.description)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {signInError && (
          <Alert variant="destructive">
            <AlertDescription>{signInError}</AlertDescription>
          </Alert>
        )}
        <Button
          onClick={handleSignIn}
          disabled={isSigningIn}
          className="w-full"
        >
          {isSigningIn
            ? t(($) => $.msIntegration.signIn.signingIn)
            : t(($) => $.msIntegration.signIn.button)}
        </Button>
        <Button variant="outline" onClick={handleSkip} className="w-full">
          {t(($) => $.msIntegration.signIn.skipUserMigration)}
        </Button>
      </CardContent>
    </Card>
  );
}
